import React, { PropsWithChildren } from 'react';

import { DisplayablePlane, isRoofPlane } from '@paragon/layout';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../../components/hooks';
import {
  clearHoveredPlaneStatesIfActive,
  setHoveredPlaneStateIfApplicable,
} from '../../../../store/actions/layout/genericPlaneActions';
import { handlePlanePicked } from '../../../../store/actions/layout/layoutPickingActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemRightClick,
} from '../../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../../../store/actions/viewportActions';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import { getCurrentlyPickablePlaneGuids } from '../../../../store/selectors/roofAndCeilingPlaneSelectors';
import {
  hoveredPlaneClearedDuringPlanePicking,
  hoveredPlaneSetDuringPlanePicking,
} from '../../../../store/slices/applicationSlice';
import { threeJsDom } from '../../../util/events/ThreeJSDom';

type Props = PropsWithChildren<{
  plane: DisplayablePlane;
}>;

export function PlaneEventHandlers({ plane, children }: Props) {
  const dispatch = useAppDispatch();

  const pickablePlaneGuids = useAppSelector(getCurrentlyPickablePlaneGuids);
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  if (!('guid' in plane)) return children;

  const type = isRoofPlane(plane) ? 'roofPlane' : 'ceilingPlane';

  // we want the mouse to "pass through" items that can't be picked. This makes multiple roof planes on the same bearing
  //   work much better in some cases, and also prevents nasty stitching artifacts when the roof plane being cut is
  //   overlapping with the cut plane being picked.
  const hoverDisabled = pickingObjectInfo?.type === 'pickingPlane' && !pickablePlaneGuids.has(plane.guid);

  return (
    <group
      onClick={async (event) => {
        if (!threeJsDom.shouldDispatchClickEvent()) return;

        // we might be triggering an action that will cause the visual to be removed, so best to clear the hover
        dispatch(hoveredPlaneClearedDuringPlanePicking());

        if (pickingObjectInfo?.type === 'pickingPlane') {
          if (pickablePlaneGuids.has(plane.guid)) {
            event.stopPropagation();

            await dispatch(handlePlanePicked(plane.guid));
          } else {
            // we don't want anything to happen, even selection, because picking is occurring
          }
        } else {
          event.stopPropagation();

          if (event.detail === 2) {
            dispatch(handleStandardSceneItemEditPropertiesAction(type, plane));
          } else {
            // FIXME: types are technically not correct here
            // https://github.com/ParagonTruss/ParagonCore/pull/8750#discussion_r1491402165
            dispatch(selectItem(plane, type, { intent: 'toggle', exclusive: !getModifierKeys(event).ctrlKey }));
          }
        }
      }}
      onContextMenu={async (event) => {
        if (pickingObjectInfo?.type === 'pickingPlane') return;

        await dispatch(handleStandardSceneItemRightClick(event, { type, guid: plane.guid, origin: 'fiber' }));
      }}
      onPointerOver={(event) => {
        if (hoverDisabled) return;

        event.stopPropagation();
        dispatch(ensureHoverItemIsActive({ type, guid: plane.guid }));
      }}
      onPointerMove={(event) => {
        if (hoverDisabled) return;

        event.stopPropagation();

        // tracking hovered planes works better based on pointer move rather than over, so we handle it separately
        //   from the viewport slice's hover item (used only for tooltips) above

        dispatch(setHoveredPlaneStateIfApplicable(plane.guid));

        if (pickingObjectInfo?.type === 'pickingPlane') {
          const { hoveredPlane: hoveredPlanes } = pickingObjectInfo;
          if (hoveredPlanes?.type !== type || hoveredPlanes.guid !== plane.guid) {
            dispatch(hoveredPlaneSetDuringPlanePicking({ type, guid: plane.guid }));
          }
        }
      }}
      onPointerOut={() => {
        if (hoverDisabled) return;

        dispatch(clearHoverItemIfActive({ type, guid: plane.guid }));
        dispatch(clearHoveredPlaneStatesIfActive(plane.guid));
      }}
    >
      {children}
    </group>
  );
}
